import React, { useState, useMemo } from 'react';
import './RusticWood.css';
import { paginateMenu } from '../../../utils/menuPagination';
import { exportPtPagesToPdf } from '../../../utils/exportPtPagesToPdf';
import ExportPdfFab from '../../../components/ExportPdfFab';

const RusticWood = ({ data, isSingleMenu }) => {
    const restaurant = data?.restaurant || {};
    const rawMenus = data?.menus || [];

    const [activeMenuIndex, setActiveMenuIndex] = useState(0);

    // Determine which pages to display
    const visiblePages = useMemo(() => {
        if (isSingleMenu) {
            return rawMenus.flatMap(menu => paginateMenu(menu));
        }
        const activeMenu = rawMenus[activeMenuIndex];
        return activeMenu ? paginateMenu(activeMenu) : [];
    }, [isSingleMenu, rawMenus, activeMenuIndex]);

    const handleNext = () => {
        if (activeMenuIndex < rawMenus.length - 1) {
            setActiveMenuIndex(p => p + 1);
            window.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (activeMenuIndex > 0) {
            setActiveMenuIndex(p => p - 1);
            window.scrollTo(0, 0);
        }
    };

    if (!data) return <div className="loading">Loading...</div>;

    return (
        <div className="rw-root">
            {/* Navigation - Only show in Multi-Menu Mode (Preview) */}
            {!isSingleMenu && (
                <div className="tmpl-nav-container no-print" style={{ justifyContent: 'center' }}>
                    <div className="tmpl-nav-controls">
                        <button className="tmpl-nav-btn" onClick={handlePrev} disabled={activeMenuIndex === 0}>
                            ← Prev Menu
                        </button>
                        <div className="tmpl-page-indicator">
                            {rawMenus[activeMenuIndex]?.name || `Menu ${activeMenuIndex + 1}`}
                            <span style={{ fontSize: '0.8em', opacity: 0.7, marginLeft: '8px' }}>
                                ({activeMenuIndex + 1}/{rawMenus.length})
                            </span>
                        </div>
                        <button className="tmpl-nav-btn" onClick={handleNext} disabled={activeMenuIndex === rawMenus.length - 1}>
                            Next Menu →
                        </button>
                    </div>
                </div>
            )}

            <ExportPdfFab onClick={() => exportPtPagesToPdf({ filename: `${restaurant.name || 'menu'}-rustic-wood.pdf` })} />

            <div className="rw-container">
                {visiblePages.map((page, index) => {
                    const categories = (page.categories || []);

                    // Header logic:
                    // PDF: Only on very first page
                    // Preview: On first page of the active menu
                    const showHeader = isSingleMenu ? (index === 0) : (page.chunkIndex === 0);

                    // Menu Title Logic: Only show on first page of the menu chunk
                    const showMenuTitle = page.chunkIndex === 0;

                    return (
                        <div key={`${page.id || page.name}-${index}`} className="rw-page pt-page active">
                            <div className="rw-wood-overlay"></div>
                            <div className="rw-content">
                                {showHeader && (
                                    <header className="rw-header">
                                        <div className="rw-logo-box">
                                            <h1 className="rw-name">{restaurant.name}</h1>
                                            {restaurant.establishedYear && <div className="rw-est">Est. {restaurant.establishedYear}</div>}
                                        </div>
                                        <div className="rw-meta">{restaurant.address} • {restaurant.phoneNumber}</div>
                                    </header>
                                )}

                                <div className="rw-body">
                                    {showMenuTitle && (
                                        <h2 className="rw-menu-title">{page.name}</h2>
                                    )}
                                    {categories.map(category => (
                                        <div key={category.id} className="rw-category">
                                            <h3 className="rw-category-title">{category.name}</h3>
                                            <div className="rw-items">
                                                {(category.items || []).map(item => (
                                                    <div key={item.id} className="rw-item">
                                                        <div className="rw-item-main">
                                                            <div className="rw-item-info">
                                                                <div className="rw-item-name-row">
                                                                    {(item.isVeg !== undefined && item.isVeg !== null) && (
                                                                        <span className={`rw-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                                                                            <span className="rw-veg-mark"></span>
                                                                        </span>
                                                                    )}
                                                                    <span className="rw-item-name">{item.name}</span>
                                                                </div>
                                                                {item.description && <p className="rw-item-desc">{item.description}</p>}
                                                            </div>
                                                            <span className="rw-item-price">{item.price}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <footer className="rw-footer">
                                    {restaurant.footerText && <p>{restaurant.footerText}</p>}
                                </footer>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RusticWood;
